/**
 * Layout Component
 * ================
 * Main application layout with sidebar and header.
 */

import { Link, Outlet, useLocation } from 'react-router-dom'
import { clsx } from 'clsx'
import {
  Package,
  FolderTree,
  MapPin,
  Factory,
  LayoutDashboard,
  Menu,
  X,
  LogIn,
  LogOut,
  Image,
  ChevronDown,
  ChevronRight,
  Boxes,
  Bike,
  Monitor,
  Server,
  Building2,
  HardDrive,
  Wifi,
  Info,
  History,
  Wrench,
  Hammer,
  Search,
  Store,
  Activity,
  Ticket,
  Clock,
} from 'lucide-react'
import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useMotorcycles } from '@/hooks/useMotorcycles'
import ThemeToggle from '@/components/ThemeToggle'
import DownloadAppButton from '@/components/DownloadAppButton'
import Footer from '@/components/Footer'

interface NavSubItem {
  label: string
  href: string
  icon: React.ReactNode
}

interface NavItem {
  label: string
  href: string
  icon: React.ReactNode
  subItems?: NavSubItem[]  // Optional nested sub-items
}

interface NavSection {
  id: string
  label: string
  icon: React.ReactNode
  items: NavItem[]
  placeholder?: boolean
}

// Static navigation sections (Inventory and Assets)
const staticNavSections: NavSection[] = [
  {
    id: 'inventory',
    label: 'Inventory',
    icon: <Boxes size={20} />,
    items: [
      { label: 'Dashboard', href: '/', icon: <LayoutDashboard size={20} /> },
      { label: 'Products', href: '/products', icon: <Package size={20} /> },
      { label: 'Categories', href: '/categories', icon: <FolderTree size={20} /> },
      { label: 'Locations', href: '/locations', icon: <MapPin size={20} /> },
      { label: 'Manufacturers', href: '/manufacturers', icon: <Factory size={20} /> },
      { label: 'Images', href: '/images', icon: <Image size={20} /> },
    ],
  },
  {
    id: 'assets',
    label: 'Assets',
    icon: <Monitor size={20} />,
    items: [
      { label: 'Dashboard', href: '/assets', icon: <LayoutDashboard size={20} /> },
      {
        label: 'All Assets',
        href: '/assets/list',
        icon: <Boxes size={20} />,
        subItems: [
          { label: 'Proxmox Hosts', href: '/assets/proxmox', icon: <Server size={20} /> },
          { label: 'Synology NAS', href: '/assets/synology', icon: <HardDrive size={20} /> },
          { label: 'UniFi Network', href: '/assets/unifi', icon: <Wifi size={20} /> },
          { label: 'Disk Health', href: '/assets/disk-health', icon: <Activity size={20} /> },
        ],
      },
      { label: 'Tickets', href: '/tickets', icon: <Ticket size={20} /> },
      { label: 'Timelog', href: '/timelog', icon: <Clock size={20} /> },
      { label: 'Locations', href: '/assets/locations', icon: <MapPin size={20} /> },
      { label: 'Manufacturers', href: '/assets/manufacturers', icon: <Building2 size={20} /> },
    ],
  },
]

/**
 * Build dynamic motorcycle sub-items for sidebar
 */
function buildMotorcycleSubItems(bikeId: string): NavSubItem[] {
  return [
    { label: 'Information', href: `/motorcycles/${bikeId}/information`, icon: <Info size={18} /> },
    { label: 'History', href: `/motorcycles/${bikeId}/history`, icon: <History size={18} /> },
    { label: 'Service', href: `/motorcycles/${bikeId}/service`, icon: <Wrench size={18} /> },
    { label: 'Restoration/Build', href: `/motorcycles/${bikeId}/build`, icon: <Hammer size={18} /> },
    { label: 'Parts on Stock', href: `/motorcycles/${bikeId}/parts`, icon: <Package size={18} /> },
    { label: 'Parts Search', href: `/motorcycles/${bikeId}/search`, icon: <Search size={18} /> },
  ]
}

const STORAGE_KEY = 'exodus-sidebar-sections'
const STORAGE_KEY_SUBITEMS = 'exodus-sidebar-subitems'

function getInitialExpandedSections(): Record<string, boolean> {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      return JSON.parse(saved)
    }
  } catch {
    // Ignore parse errors
  }
  // Default: Inventory expanded, others collapsed
  return { inventory: true, assets: false, motorcycles: false }
}

function getInitialExpandedSubItems(): Record<string, boolean> {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_SUBITEMS)
    if (saved) {
      return JSON.parse(saved)
    }
  } catch {
    // Ignore parse errors
  }
  // Default: All Assets sub-items collapsed
  return {}
}

export default function Layout() {
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { isAuthenticated, user, isLoading, login, logout, isLoggingOut } = useAuth()
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(getInitialExpandedSections)
  const [expandedSubItems, setExpandedSubItems] = useState<Record<string, boolean>>(getInitialExpandedSubItems)

  // Fetch motorcycles for dynamic sidebar
  const { data: motorcycles } = useMotorcycles()

  // Build nav sections with dynamic motorcycles
  const navSections = useMemo<NavSection[]>(() => {
    // Start with static sections
    const sections = [...staticNavSections]

    // Build motorcycles section with dynamic bike list
    const motorcycleItems: NavItem[] = [
      { label: 'Dashboard', href: '/motorcycles', icon: <LayoutDashboard size={20} /> },
    ]

    // Add each motorcycle with its sub-pages
    if (motorcycles && motorcycles.length > 0) {
      motorcycles.forEach((bike) => {
        motorcycleItems.push({
          label: `${bike.name} (${bike.year})`,
          href: `/motorcycles/${bike.motorcycle_id}/information`,
          icon: <Bike size={20} />,
          subItems: buildMotorcycleSubItems(bike.motorcycle_id),
        })
      })
    }

    // Add management items at the end
    motorcycleItems.push(
      { label: 'Locations', href: '/moto/locations', icon: <MapPin size={20} /> },
      { label: 'Manufacturers', href: '/moto/manufacturers', icon: <Factory size={20} /> },
      { label: 'Vendors', href: '/moto/vendors', icon: <Store size={20} /> }
    )

    sections.push({
      id: 'motorcycles',
      label: 'Motorcycles',
      icon: <Bike size={20} />,
      items: motorcycleItems,
    })

    return sections
  }, [motorcycles])

  // Persist expanded state to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(expandedSections))
  }, [expandedSections])

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_SUBITEMS, JSON.stringify(expandedSubItems))
  }, [expandedSubItems])

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }))
  }

  const toggleSubItem = (itemHref: string) => {
    setExpandedSubItems((prev) => ({
      ...prev,
      [itemHref]: !prev[itemHref],
    }))
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-200 ease-in-out lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b dark:border-gray-700">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
              <span className="text-white font-bold text-lg">E</span>
            </div>
            <span className="text-xl font-semibold text-gray-900 dark:text-white">Exodus</span>
          </Link>
          <button
            className="lg:hidden p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2">
          {navSections.map((section) => {
            const isExpanded = expandedSections[section.id] ?? false

            return (
              <div key={section.id}>
                {/* Section header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                >
                  {isExpanded ? (
                    <ChevronDown size={16} className="text-gray-400" />
                  ) : (
                    <ChevronRight size={16} className="text-gray-400" />
                  )}
                  {section.icon}
                  <span className="font-medium">{section.label}</span>
                </button>

                {/* Section items */}
                {isExpanded && (
                  <div className="ml-6 mt-1 space-y-1">
                    {section.placeholder ? (
                      <div className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500 italic">
                        Coming soon
                      </div>
                    ) : (
                      section.items.map((item) => {
                        const isActive =
                          location.pathname === item.href ||
                          (item.href !== '/' && location.pathname.startsWith(item.href))
                        const hasSubItems = item.subItems && item.subItems.length > 0
                        const isSubItemExpanded = expandedSubItems[item.href] ?? false
                        // Check if any sub-item is active (to highlight parent)
                        const isSubItemActive = hasSubItems && item.subItems?.some(
                          (sub) => location.pathname === sub.href || location.pathname.startsWith(sub.href)
                        )

                        return (
                          <div key={item.href}>
                            {hasSubItems ? (
                              // Item with sub-items: render as expandable
                              <>
                                <div className="flex items-center">
                                  <button
                                    onClick={() => toggleSubItem(item.href)}
                                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 mr-1"
                                  >
                                    {isSubItemExpanded ? (
                                      <ChevronDown size={14} className="text-gray-400" />
                                    ) : (
                                      <ChevronRight size={14} className="text-gray-400" />
                                    )}
                                  </button>
                                  <Link
                                    to={item.href}
                                    className={clsx(
                                      'flex-1 flex items-center gap-3 px-2 py-2 rounded-lg transition-colors',
                                      isActive && !isSubItemActive
                                        ? 'bg-primary-50 text-primary-700 font-medium dark:bg-primary-900/50 dark:text-primary-400'
                                        : isSubItemActive
                                          ? 'text-primary-600 font-medium dark:text-primary-400'
                                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white'
                                    )}
                                    onClick={() => setSidebarOpen(false)}
                                  >
                                    {item.icon}
                                    {item.label}
                                  </Link>
                                </div>
                                {/* Sub-items */}
                                {isSubItemExpanded && (
                                  <div className="ml-6 mt-1 space-y-1">
                                    {item.subItems?.map((subItem) => {
                                      const isSubActive = location.pathname === subItem.href

                                      return (
                                        <Link
                                          key={subItem.href}
                                          to={subItem.href}
                                          className={clsx(
                                            'flex items-center gap-3 px-3 py-1.5 rounded-lg transition-colors text-sm',
                                            isSubActive
                                              ? 'bg-primary-50 text-primary-700 font-medium dark:bg-primary-900/50 dark:text-primary-400'
                                              : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-white'
                                          )}
                                          onClick={() => setSidebarOpen(false)}
                                        >
                                          {subItem.icon}
                                          {subItem.label}
                                        </Link>
                                      )
                                    })}
                                  </div>
                                )}
                              </>
                            ) : (
                              // Regular item without sub-items
                              <Link
                                to={item.href}
                                className={clsx(
                                  'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
                                  isActive
                                    ? 'bg-primary-50 text-primary-700 font-medium dark:bg-primary-900/50 dark:text-primary-400'
                                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white'
                                )}
                                onClick={() => setSidebarOpen(false)}
                              >
                                {item.icon}
                                {item.label}
                              </Link>
                            )}
                          </div>
                        )
                      })
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </nav>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64 min-h-screen flex flex-col">
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 bg-white dark:bg-gray-800 border-b dark:border-gray-700 flex items-center px-4 lg:px-6 transition-colors">
          <button
            className="lg:hidden p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400 mr-4"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={20} />
          </button>

          <div className="flex-1" />

          {/* User menu */}
          <div className="flex items-center gap-2">
            {/* Download app button (only shown in browser) */}
            <DownloadAppButton />

            {/* Theme toggle */}
            <ThemeToggle />

            {isLoading ? (
              <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse" />
            ) : isAuthenticated && user ? (
              <>
                <div className="hidden sm:flex items-center gap-2 ml-2">
                  {user.profile_picture_url ? (
                    <img
                      src={user.profile_picture_url}
                      alt={user.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                      <span className="text-sm font-medium text-primary-700 dark:text-primary-400">
                        {user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {user.name}
                  </span>
                </div>
                <button
                  onClick={() => logout()}
                  disabled={isLoggingOut}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Logout"
                >
                  <LogOut size={18} />
                  <span className="hidden sm:inline">Logout</span>
                </button>
              </>
            ) : (
              <button
                onClick={() => login(window.location.pathname)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
              >
                <LogIn size={18} />
                <span>Login</span>
              </button>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6 flex-1">
          <Outlet />
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  )
}
